import { storage } from '../storage';
import type { MembershipSummary, TokenResponse } from './types';

/** What a signed-in session persists between page loads. */
export interface StoredSession {
  accessToken: string;
  expiresAt: string;
  refreshToken: string | null;
  refreshExpiresAt: string | null;
  membership: MembershipSummary | null;
}

/**
 * Session storage for one application.
 *
 * The four clients run on different ports in development and different hostnames in production, but a
 * shared hostname is a supported deployment too, so each app namespaces its key. Without that, opening the
 * staff app would sign you out of the guardian app on the same device.
 *
 * Tokens live in `localStorage`. That is a deliberate trade-off, not an oversight: the alternative — an
 * httpOnly refresh cookie — needs the API to set cookies on every request from four app hosts, and conflicts
 * with the native wrappers planned as a fast follow. The access
 * token is short-lived and the refresh token rotates on every use, so a stolen pair is detectable
 * (presenting a rotated token revokes the whole chain, see AuthEndpoints.RefreshAsync).
 */
export class TokenStore {
  private readonly key: string;
  private cached: StoredSession | null = null;
  private loaded = false;

  constructor(appKey: string) {
    this.key = `daghfal.${appKey}.session`;
  }

  read(): StoredSession | null {
    if (this.loaded) return this.cached;
    this.loaded = true;
    // Unreadable storage (a private window, blocked site data, corrupt JSON) is treated as signed out rather
    // than crashing the application shell on first paint.
    this.cached = storage.readJson<StoredSession>(this.key) ?? null;
    return this.cached;
  }

  /**
   * The session as storage holds it now, past this tab's copy. Another tab, a window a push opened, or the page the
   * browser restored may have refreshed the tokens a moment ago, and its storage event may not have arrived yet.
   */
  reload(): StoredSession | null {
    this.loaded = false;
    return this.read();
  }

  write(tokens: TokenResponse, membership: MembershipSummary | null): StoredSession {
    const session: StoredSession = {
      accessToken: tokens.accessToken,
      expiresAt: tokens.expiresAt,
      refreshToken: tokens.refreshToken,
      refreshExpiresAt: tokens.refreshExpiresAt,
      membership,
    };
    this.cached = session;
    this.loaded = true;
    // Where storage is unavailable the session still works for this tab; it just will not survive a reload.
    storage.writeJson(this.key, session);
    return session;
  }

  clear(): void {
    this.cached = null;
    this.loaded = true;
    storage.remove(this.key);
  }

  /** Notifies this tab when another tab signs in or out, so the shell can follow. */
  subscribe(onChange: () => void): () => void {
    const handler = (event: StorageEvent) => {
      if (event.key !== this.key) return;
      this.loaded = false;
      onChange();
    };
    globalThis.addEventListener?.('storage', handler);
    return () => globalThis.removeEventListener?.('storage', handler);
  }
}
